import { useState, createContext, useContext, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useThemeMode } from '@/contexts/ThemeContext'
import useIsMobile from '@/hooks/useIsMobile'
import { theme } from '@/lib/theme'

interface RepresentationHelpContextType {
  helpEnabled: boolean
  toggleHelp: () => void
}

const RepresentationHelpContext = createContext<RepresentationHelpContextType>({
  helpEnabled: false,
  toggleHelp: () => {},
})

export const useRepresentationHelp = () => useContext(RepresentationHelpContext)

interface HelpProviderProps {
  children: React.ReactNode
}

export function RepresentationHelpProvider({ children }: HelpProviderProps) {
  const [helpEnabled, setHelpEnabled] = useState(false)

  const toggleHelp = () => {
    setHelpEnabled((prev) => !prev)
  }

  return (
    <RepresentationHelpContext.Provider value={{ helpEnabled, toggleHelp }}>
      {children}
    </RepresentationHelpContext.Provider>
  )
}

interface HelpToggleProps {
  style?: React.CSSProperties
}

export function RepresentationHelpToggle({ style }: HelpToggleProps) {
  const { t } = useTranslation()
  const { helpEnabled, toggleHelp } = useRepresentationHelp()
  const { colors } = useThemeMode()
  const isMobile = useIsMobile()
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 })
  const iconRef = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (showTooltip && iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect()
      const top = rect.top - 8
      const left = rect.left + rect.width / 2
      setTooltipPos({ top, left })
    }
  }, [showTooltip])

  // Hide tooltip only on mobile, but keep the toggle button
  return (
    <>
      <span
        ref={iconRef}
        onClick={toggleHelp}
        style={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          ...style,
        }}
        onMouseEnter={() => !isMobile && setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            backgroundColor: helpEnabled ? colors.primaryAccent : colors.primaryBg,
            color: helpEnabled ? colors.primaryBg : colors.primaryAccent,
            border: `2px solid ${colors.primaryAccent}`,
            fontSize: '16px',
            fontWeight: 'bold',
            fontFamily: 'monospace',
            transition: 'all 0.2s ease',
          }}
        >
          ?
        </span>
      </span>
      {showTooltip && createPortal(
        <span
          style={{
            position: 'fixed',
            zIndex: 100001,
            top: `${tooltipPos.top}px`,
            left: `${tooltipPos.left}px`,
            transform: 'translate(-50%, -100%)',
            padding: '8px 12px',
            backgroundColor: colors.primaryBg,
            color: colors.primaryText,
            border: `1px solid ${colors.primaryBorder}`,
            borderRadius: '6px',
            fontSize: theme.fontSize.xs,
            fontFamily: '"Departure Mono", monospace',
            maxWidth: '280px',
            whiteSpace: 'nowrap',
            lineHeight: '1.4',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            pointerEvents: 'none',
          }}
        >
          {t('help.representation.tooltip')}
        </span>,
        document.body
      )}
    </>
  )
}